/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnInit } from '@angular/core';
import { OktaUserService } from 'src/app/shared/services/oktauser.service';
import { CybersocxdrService } from '../../shared/services/xtendedsoc/cybersocxdr.service';
import * as Highcharts from 'highcharts';
import highcharts3D from 'highcharts/highcharts-3d';
import HC_organization from 'highcharts/modules/organization';
import { getDefaultHighchartOptions } from '../utils';
import { ClientInfos } from '../../shared/model/cybersocxdr/microsocClientInfo';
import { OrganizationNode } from 'src/app/shared/model/cybersocxdr/organizationNode';

// eslint-disable @typescript-eslint/naming-convention
const technologiesMap = {
  'Rapid7 InsightIDR': { iconPath: '/assets/images/icons/rapid7.svg' },
  'Cortex XDR': { iconPath: '/assets/images/icons/palo.svg' },
  'Cortex XSOAR': { iconPath: '/assets/images/icons/palo.svg' },
  'Crowdstrike falcon': { iconPath: '/assets/images/icons/crowdstrike.svg' },
  'Tehtris XDR': { iconPath: '/assets/images/icons/tehtris.svg' },
  'Sentinel One': { iconPath: '/assets/images/icons/sentinelone.svg' },
  Sekoia: { iconPath: '/assets/images/icons/sekoia.svg' },
  Grafana: { iconPath: '/assets/images/icons/grafana.svg' },
  'Harfanglab EDR': { iconPath: '/assets/images/icons/harfanglab.svg' },
};

const roleImageMap = {
  'MicroSOC Manager': '/assets/images/icons/microsocmanager.svg',
  'Delivery Manager': '/assets/images/icons/deliverymanager.svg',
  'SOC Analyst - Referrers': '/assets/images/icons/socanalyst.svg',
  'Urgent Call': '/assets/images/icons/urgentcall.svg',
};

highcharts3D(Highcharts);
HC_organization(Highcharts);

@Component({
  selector: 'app-cybersocxdashboard',
  templateUrl: './cybersocxdrcontact.component.html',
  styleUrls: ['./cybersocxdrcontact.component.scss'],
})
export class CybersocxdrContactComponent implements OnInit {
  highcharts: typeof Highcharts = Highcharts;
  organizationData: OrganizationNode;
  organizationOptions: Highcharts.Options;

  clientContactData: Array<object> = [];
  clientTechnologies: Array<object> = [];

  // Dummy client information
  clientInfo: ClientInfos = {};
  selectedTenant: string;

  constructor(private cybersocxdrService: CybersocxdrService, private oktaUserService: OktaUserService) {}

  ngOnInit() {
    const tens = JSON.parse(localStorage.getItem('okta-tenants'));
    if (tens && tens.selectedTenant) {
      this.selectedTenant = tens.selectedTenant;
    } else {
      this.oktaUserService.listGroups().then((groups) => {
        const userGroupsTok = groups.filter((g) => g.profile.name.startsWith('USERS_'));
        if (userGroupsTok.length === 1) {
          this.selectedTenant = userGroupsTok[0].split('_')[1];
        }
      });
    }

    Highcharts.setOptions(getDefaultHighchartOptions());

    this.init();
  }

  async init() {
    this.clientInfo = await this.cybersocxdrService.getClientInfo();
    this.clientTechnologies = (await this.cybersocxdrService.getClientTechnologies()).map((technology) => ({
      ...technology,
      path: technologiesMap[technology.brand].iconPath,
    }));

    this.getOrganizationData();
  }

  /** Get the organization data and generate the organization options from it */
  async getOrganizationData() {
    this.clientContactData = await this.cybersocxdrService.getClientContacts();
    this.organizationData = await this.cybersocxdrService.getOrganization();
    this.drawOrganization();
  }

  /** Update the organization options, triggering a redraw at the same time */
  drawOrganization() {
    this.organizationOptions = this.generateOptions();
  }

  /** Build the backend data into highchart node data */
  buildNodeData(data?: OrganizationNode): Highcharts.SeriesSankeyNodesOptionsObject[] {
    if (!data) {
      return [];
    }

    return [
      {
        id: `${data.name}_${data.phone}_${data.role}`,
        title: data.role,
        name: data.name,
        description: data.phone,
        image: roleImageMap[data.role],
      },
      ...(data?.children?.flatMap((child) => this.buildNodeData(child)) ?? []),
    ];
  }

  /** Build the backend data into highchart link between nodes data */
  buildNodeLinks(data?: OrganizationNode): Highcharts.SeriesSankeyPointOptionsObject[] {
    if (!data || !data.children || data.children.length === 0) {
      return [];
    }

    return [
      ...data.children.map((child) => ({
        from: `${data.name}_${data.phone}_${data.role}`,
        to: `${child.name}_${child.phone}_${child.role}`,
      })),
      ...data.children.flatMap((child) => this.buildNodeLinks(child)),
    ];
  }

  /** Generate an highcharts option object */
  generateOptions(): Highcharts.Options {
    return {
      chart: { inverted: true },
      title: { text: '' },
      accessibility: {
        point: {
          descriptionFormatter: (point: Highcharts.Point | any) => {
            const nodeName = point.toNode.name;
            const nodeId = point.toNode.id;
            const nodeDesc = nodeName === nodeId ? nodeName : nodeName + ', ' + nodeId;
            const parentDesc = point.fromNode.id;
            return point.index + '. ' + nodeDesc + ', reports to ' + parentDesc + '.';
          },
        },
      },

      series: [
        {
          type: 'organization',
          name: 'Orange Cyberdefense',
          keys: ['from', 'to'],
          data: this.buildNodeLinks(this.organizationData),
          levels: [
            {
              level: 0,
              color: 'silver',
              dataLabels: {
                color: 'black',
                style: {
                  fontSize: '10px',
                },
              },
            },
            {
              level: 1,
              color: 'silver',
              dataLabels: {
                color: 'black',
                style: {
                  fontSize: '10px',
                },
              },
            },
            {
              level: 2,
              dataLabels: {
                color: 'black',
                style: {
                  fontSize: '10px',
                },
              },
              color: '#FF7900',
            },
            {
              level: 4,
              color: '#FF7900',
            },
          ],
          nodes: this.buildNodeData(this.organizationData),
          colorByPoint: false,
          color: '#007ad0',
          dataLabels: {
            color: 'white',
          },
          borderColor: null,
          nodeWidth: 100,
        },
      ],
      tooltip: { outside: true },
      exporting: { enabled: false },
    };
  }
}
